import { CacheStrategy } from '@rammetec/data-access';
import { AUDIT_FIELD_DESCRIPTORS, AUDIT_FOREIGN_KEYS } from './_auditFields.js';
import { BUSINESS_ALLOWED_DATA_SOURCES, BUSINESS_DEFAULT_DATA_SOURCE } from './dataSourcePolicy.js';
import { PRODUCTION_ORDER_STEP, ProductionOrderLineSourceValues, PRODUCTION_ORDER_LINE_SOURCE_LABELS, } from '../types/production-order-step.js';
import { FIELD_LIMITS, MAX_DURATION_MS } from '../validation/fieldLimits.js';
/**
 * Descriptor for ProductionOrderStep (planned process steps under an OP).
 * Subcollection: org/{orgId}/production-order/{productionOrderId}/step
 *
 * Resolve repository with context: { orgId, productionOrderId }.
 */
export const productionOrderStepDescriptor = {
    name: PRODUCTION_ORDER_STEP,
    label: 'Etapa da OP',
    domain: 'production',
    collectionPath: 'org/{orgId}/production-order/{productionOrderId}/step',
    pathPatternKeys: {
        orgId: 'string',
        productionOrderId: 'string',
    },
    version: 2,
    description: 'Planned process step of a production order — snapshot at release (spec 0040 §2.3)',
    repositoryConfig: {
        dataSource: BUSINESS_DEFAULT_DATA_SOURCE,
        // NoCache: filho de entidade versionada (spec 0045) — itens em cache voltariam velhos após Recarregar e seriam regravados por cima.
        cache: CacheStrategy.NoCache,
    },
    allowedDataSources: BUSINESS_ALLOWED_DATA_SOURCES,
    fields: [
        { name: 'id', label: 'ID', type: 'string', required: true },
        {
            name: 'productionOrderId',
            label: 'Ordem de produção',
            type: 'string',
            required: true,
            description: 'Parent production order id (also encoded in the path)',
        },
        {
            name: 'order',
            example: 2,
            label: 'Ordem',
            type: 'number',
            required: true,
            description: 'Execution order (1-based, editable — no drag in v1)',
        },
        {
            name: 'processId',
            label: 'Processo',
            type: 'string',
            required: true,
            description: 'Process for this step',
        },
        {
            name: 'machineId',
            label: 'Máquina',
            type: 'string',
            required: false,
            description: 'Suggested machine (copied from the sheet only when the step has exactly one)',
        },
        {
            name: 'expectedProductionTime',
            example: 2700000,
            label: 'Tempo de produção esperado',
            type: 'number',
            display: 'duration',
            required: false,
            description: `Expected production time in milliseconds (0..${MAX_DURATION_MS}; null = not informed)`,
        },
        {
            name: 'expectedSetupTime',
            example: 900000,
            label: 'Tempo de setup esperado',
            type: 'number',
            display: 'duration',
            required: false,
            description: `Expected setup time in milliseconds (0..${MAX_DURATION_MS}; null = not informed)`,
        },
        {
            name: 'processInstructions',
            example: 'Tingimento em jet — receita Vermelho Carmim',
            label: 'Instruções de processo',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.DESCRIPTION,
        },
        {
            name: 'source',
            example: 'sheet',
            label: 'Origem',
            type: 'enum',
            required: true,
            enumValues: [...ProductionOrderLineSourceValues],
            enumLabels: PRODUCTION_ORDER_LINE_SOURCE_LABELS,
            description: "Provenance: 'sheet' (release snapshot) or 'manual' (typed after release)",
        },
        {
            name: 'producesOutput',
            example: true,
            label: 'Entrega produto acabado',
            type: 'boolean',
            required: false,
            defaultValue: false,
            description: 'Step that posts finished goods to stock (F4-D1). Release default: true only on the last sheet step.',
        },
        ...AUDIT_FIELD_DESCRIPTORS,
    ],
    foreignKeys: [
        {
            fieldName: 'processId',
            targetEntity: 'Process',
            relationship: 'many-to-one',
            displayField: 'description',
            description: 'Processo da etapa',
        },
        {
            fieldName: 'machineId',
            targetEntity: 'Machine',
            relationship: 'many-to-one',
            displayField: 'description',
            description: 'Máquina sugerida da etapa',
        },
        {
            fieldName: 'productionOrderId',
            targetEntity: 'ProductionOrder',
            relationship: 'many-to-one',
            displayField: 'code',
            description: 'Ordem de produção pai',
        },
        ...AUDIT_FOREIGN_KEYS,
    ],
};
//# sourceMappingURL=production-order-step.descriptor.js.map